import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Stack,
    Menu,
    MenuItem,
    Avatar,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyIcon from '@mui/icons-material/Key';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { APP_ROLE, canAccessRole } from '../constants/roles';
import { fetchParticipationCancellationSummary } from '../controllers/participationCancellationController';

const navItems = [
    { label: 'Dashboard', to: '/', icon: <DashboardOutlinedIcon />, requiredRole: null },
    { label: 'Kullanıcılar', to: '/users', icon: <PersonOutlineIcon />, requiredRole: 'Kullanici' },
    { label: 'Duyurular', to: '/announcements', icon: <CampaignOutlinedIcon />, requiredRole: 'Duyuru' },
    {
        label: 'Gruplar & Üyeler',
        icon: <GroupsOutlinedIcon />,
        requiredRole: 'Uyeler',
        children: [
            { label: 'Gruplar', to: '/groups' },
            { label: 'Üyeler', to: '/members' },
        ],
    },
    {
        label: 'İçerik Yönetimi',
        icon: <ArticleOutlinedIcon />,
        requiredRole: 'Icerik',
        children: [
            { label: 'Haberler', to: '/news' },
            { label: 'Bültenler', to: '/newsletters' },
            { label: 'Başkanlarımız', to: '/presidents' },
            { label: 'Yönetim Kurulu', to: '/boards' },
            { label: 'Galeriler', to: '/galleries' },
        ],
    },
    { label: 'Projeler', to: '/projects', icon: <FolderOutlinedIcon />, requiredRole: 'Projeler' },
    {
        label: 'Bildirim Yönetimi',
        icon: <NotificationsOutlinedIcon />,
        requiredRole: 'Bildirimler',
        children: [
            { label: 'E-postalar', to: '/notifications/emails' },
            { label: 'Uygulama Bildirimleri', to: '/notifications/app-notifications' },
            { label: 'SMS Mesajları', to: '/notifications/sms' },
        ],
    },
    {
        label: 'Etkinlikler',
        icon: <EventOutlinedIcon />,
        requiredRole: 'Etkinlik',
        children: [
            { label: 'Etkinlikler', to: '/events' },
            { label: 'İptal Talepleri', to: '/events/cancellation-requests', badgeKey: 'cancellation' },
            { label: 'Haftalık Toplantılar', to: '/events/weekly-meetings' },
        ],
    },
    { label: 'Anketler', to: '/surveys', icon: <QuizOutlinedIcon />, requiredRole: 'Anket' },
    {
        label: 'Ödemeler',
        icon: <PaymentIcon />,
        requiredRole: APP_ROLE.PAYMENTS,
        children: [
            { label: 'Aidatlar', to: '/payments/dues' },
            { label: 'Haftalık Toplantılar', to: '/payments/weekly-meetings' },
            { label: 'Atlanan otomatik borçlar', to: '/payments/skipped-meal-debts' },
            { label: 'Etkinlik ödemeleri', to: '/payments/event-dues' },
            { label: 'Bağış Kartları', to: '/payments/donation-cards' },
            { label: 'Proje Ödemeleri', to: '/payments/project-payments' },
            { label: 'Çeşitli Ödemeler', to: '/payments/misc' },
        ],
    },
];

const Sidebar = ({ onDrawerToggle, onPasswordChange, onLogout }) => {
    const location = useLocation();
    const { hasRole, username } = useAuth();

    const [groupsOpen, setGroupsOpen] = useState(false);
    const [contentOpen, setContentOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [paymentsOpen, setPaymentsOpen] = useState(false);
    const [eventsOpen, setEventsOpen] = useState(false);
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [pendingCancellationCount, setPendingCancellationCount] = useState(0);

    useEffect(() => {
        if (!canAccessRole(hasRole, 'Etkinlik')) return undefined;
        let active = true;
        fetchParticipationCancellationSummary()
            .then((data) => {
                if (active) setPendingCancellationCount(data?.pendingCount ?? 0);
            })
            .catch(() => {
                if (active) setPendingCancellationCount(0);
            });
        return () => { active = false; };
    }, [hasRole]);

    // Filter nav items based on user roles
    const filteredNavItems = navItems.filter((item) => canAccessRole(hasRole, item.requiredRole));

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: 2 }}>
            {/* Logo Section */}
            <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Link
                    to="/"
                    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                >
                    <Box
                        component="img"
                        src="/assets/images/main-logo.svg"
                        alt="Rotary Admin"
                        sx={{
                            height: 60,
                            width: 'auto',
                            margin: '0 auto',
                            cursor: 'pointer',
                        }}
                    />
                </Link>
                <IconButton
                    onClick={onDrawerToggle}
                    sx={{ display: { sm: 'none' } }}
                >
                    <MenuIcon />
                </IconButton>
            </Box>

            {/* Navigation Items */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <List sx={{ pt: 1 }}>
                    {filteredNavItems.map((item) => {
                        if (!item.children) {
                            return (
                                <ListItem key={item.to} disablePadding>
                                    <ListItemButton
                                        component={Link}
                                        to={item.to}
                                        selected={location.pathname === item.to}
                                        sx={{
                                            py: .6,
                                            my: .1,
                                            px: 1,
                                            border: '1px solid transparent',
                                            borderRadius: '5px',
                                            boxSizing: 'border-box',
                                            '&:hover': {
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e8e8e8',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                border: '1px solid transparent',
                                                '&:hover': {
                                                    backgroundColor: 'primary.dark',
                                                    border: '1px solid transparent',
                                                },
                                                '& .MuiListItemIcon-root': {
                                                    color: 'white',
                                                },
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit', minWidth: 'auto', mr: '10px', '& svg': { fontSize: '20px' } }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.label} sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        }
                        return (
                            <React.Fragment key={item.label}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => {
                                            if (item.label === 'Gruplar & Üyeler') setGroupsOpen((o) => !o);
                                            else if (item.label === 'İçerik Yönetimi') setContentOpen((o) => !o);
                                            else if (item.label === 'Bildirim Yönetimi') setNotificationsOpen((o) => !o);
                                            else if (item.label === 'Ödemeler') setPaymentsOpen((o) => !o);
                                            else if (item.label === 'Etkinlikler') setEventsOpen((o) => !o);
                                        }}
                                        sx={{
                                            py: .6,
                                            my: .1,
                                            px: 1,
                                            border: '1px solid transparent',
                                            borderRadius: '5px',
                                            boxSizing: 'border-box',
                                            '&:hover': {
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e8e8e8',
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit', minWidth: 'auto', mr: '10px', '& svg': { fontSize: '20px' } }}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.label} sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }} />
                                        <Box sx={{ '& svg': { fontSize: '20px' } }}>
                                            {(item.label === 'Gruplar & Üyeler'
                                                ? groupsOpen
                                                : item.label === 'İçerik Yönetimi'
                                                    ? contentOpen
                                                    : item.label === 'Bildirim Yönetimi'
                                                        ? notificationsOpen
                                                        : item.label === 'Etkinlikler'
                                                            ? eventsOpen
                                                            : paymentsOpen) ? <ExpandLess /> : <ExpandMore />}
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                                <Collapse
                                    in={
                                        item.label === 'Gruplar & Üyeler'
                                            ? groupsOpen
                                            : item.label === 'İçerik Yönetimi'
                                                ? contentOpen
                                                : item.label === 'Bildirim Yönetimi'
                                                    ? notificationsOpen
                                                    : item.label === 'Etkinlikler'
                                                        ? eventsOpen
                                                        : paymentsOpen
                                    }
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <List component="div" disablePadding>
                                        {item.children.map((child) => (
                                            <ListItem key={child.to} disablePadding>
                                                <ListItemButton
                                                    component={Link}
                                                    to={child.to}
                                                    selected={location.pathname === child.to}
                                                    sx={{
                                                        py: .6,
                                                        my: .1,
                                                        px: 1,
                                                        pl: 4,
                                                        border: '1px solid transparent',
                                                        borderRadius: '5px',
                                                        boxSizing: 'border-box',
                                                        '&:hover': {
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #e8e8e8',
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'primary.main',
                                                            color: 'white',
                                                            border: '1px solid transparent',
                                                            '&:hover': {
                                                                backgroundColor: 'primary.dark',
                                                                border: '1px solid transparent',
                                                            },
                                                            '& .MuiListItemIcon-root': {
                                                                color: 'white',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={(
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <span>{child.label}</span>
                                                                {child.badgeKey === 'cancellation' && pendingCancellationCount > 0 && (
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            ml: 0.5,
                                                                            px: 0.75,
                                                                            py: 0.1,
                                                                            borderRadius: '10px',
                                                                            fontSize: '11px',
                                                                            fontWeight: 600,
                                                                            backgroundColor: '#F8285A',
                                                                            color: '#fff',
                                                                            lineHeight: 1.4,
                                                                        }}
                                                                    >
                                                                        {pendingCancellationCount}
                                                                    </Box>
                                                                )}
                                                            </Stack>
                                                        )}
                                                        sx={{ '& .MuiTypography-root': { fontSize: '14px', fontWeight: 500 } }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    })}
                </List>
            </Box>

            {/* Bottom Actions */}
            <Box sx={{ borderTop: '1px solid #e4e4e4' }}>
                <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    {/* Sol taraf - Kullanıcı Bilgisi ve Dropdown */}
                    <ListItemButton
                        onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                        sx={{
                            flex: 1,
                            py: 1,
                            px: 1.5,
                            border: '1px solid transparent',
                            borderRadius: '5px',
                            boxSizing: 'border-box',
                            '&:hover': {
                                backgroundColor: '#ffffff',
                                border: '1px solid #e8e8e8',
                            },
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                mr: 1,
                                bgcolor: 'primary.main',
                            }}
                        >
                            <AccountCircleIcon sx={{ fontSize: '20px' }} />
                        </Avatar>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, flex: 1, textAlign: 'left' }}>
                            {username || 'Kullanıcı'}
                        </Typography>
                    </ListItemButton>

                    {/* Sağ taraf - Tema ve Çıkış */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* <Box
                            sx={{
                                border: '1px solid transparent',
                                borderRadius: '5px',
                                boxSizing: 'border-box',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e8e8e8',
                                },
                                '& .MuiIconButton-root': {
                                    ml: 0,
                                },
                            }}
                        >
                            <ThemeToggle />
                        </Box> */}
                        <IconButton
                            onClick={onLogout}
                            sx={{
                                color: 'inherit',
                                border: '1px solid transparent',
                                borderRadius: '5px',
                                boxSizing: 'border-box',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e8e8e8',
                                },
                            }}
                        >
                            <LogoutIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {/* Kullanıcı Dropdown Menu */}
            <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: '5px',
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        setUserMenuAnchor(null);
                        onPasswordChange?.();
                    }}
                    sx={{
                        py: 1,
                        px: 2,
                        fontSize: '14px',
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                        <KeyIcon sx={{ fontSize: '18px' }} />
                    </ListItemIcon>
                    <ListItemText primary="Şifre Değiştir" />
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default Sidebar;
